/**
 * gammaEvaluationCycle.ts — MER Algo Footprints v7.1B — Phase 6.5.2
 *
 * Canonical evaluation cycle identity for Gamma attribution correlation.
 *
 * ── Purpose ───────────────────────────────────────────────────────────────────
 * Provides a single explicit evaluationCycleId per symbol evaluation cycle so
 * that confidence attribution (emitted from buildSymbolContext) and ranking
 * attribution (emitted from dashboardView) can be joined by an intentional
 * shared identity rather than implicitly by timestamp proximity.
 *
 * ── Design ────────────────────────────────────────────────────────────────────
 * No randomness. The ID is a deterministic monotonic counter per symbol:
 *   '<SYMBOL>_cycle_<N>'
 * where N increments each time startEvaluationCycle is called for that symbol.
 *
 * ── Write path ────────────────────────────────────────────────────────────────
 * dashboardView calls startEvaluationCycle(symbol) once per symbol per scoring
 * pass, before calling getSymbolContext. This returns a fresh ID and stores it.
 *
 * ── Read path ─────────────────────────────────────────────────────────────────
 * buildSymbolContext calls getEvaluationCycleId(symbol) when creating attribution
 * records. It reads the ID that dashboardView already stored. If no cycle has
 * been started (e.g. in direct/test use), falls back to a timestamp-derived ID
 * so attribution still works outside the dashboardView context.
 *
 * ── Isolation ─────────────────────────────────────────────────────────────────
 * No React, no UI, no server, no async. Pure module-level state.
 * Registry is bounded: FIFO-evicts when > MAX_SYMBOLS entries.
 *
 * Placement: src/domains/market/engines/gamma/gammaEvaluationCycle.ts
 */

const MAX_SYMBOLS = 50;

// Registry: symbol → { id, counter }
const _registry = new Map<string, { id: string; counter: number }>();

function symbolKey(symbol: string): string {
  return symbol.toUpperCase();
}

/**
 * Starts a new evaluation cycle for a symbol.
 * Returns the new evaluationCycleId.
 * Called by dashboardView at the top of each symbol's scoring iteration.
 */
export function startEvaluationCycle(symbol: string): string {
  const key     = symbolKey(symbol);
  const prev    = _registry.get(key);
  const counter = (prev?.counter ?? 0) + 1;
  const id      = `${key}_cycle_${counter}`;

  _registry.set(key, { id, counter });

  // FIFO eviction when registry grows too large
  if (_registry.size > MAX_SYMBOLS) {
    const oldest = _registry.keys().next().value;
    if (oldest && oldest !== key) _registry.delete(oldest);
  }

  return id;
}

/**
 * Returns the current evaluationCycleId for a symbol.
 * Called by buildSymbolContext when creating attribution records.
 *
 * Falls back to a timestamp-derived ID when no cycle has been started
 * (direct use outside dashboardView, e.g. tests or standalone scoring).
 */
export function getEvaluationCycleId(symbol: string): string {
  const key   = symbolKey(symbol);
  const entry = _registry.get(key);
  return entry?.id ?? `${key}_cycle_ts_${Date.now()}`;
}

/**
 * Clears the registry entry for a symbol.
 * Useful in tests to ensure isolation between test runs.
 */
export function clearEvaluationCycle(symbol: string): void {
  _registry.delete(symbolKey(symbol));
}

/**
 * Clears the entire registry.
 * Useful in tests.
 */
export function clearAllEvaluationCycles(): void {
  _registry.clear();
}

/**
 * Returns the current registry size (for testing/debugging).
 */
export function evaluationCycleCount(): number {
  return _registry.size;
}
